import {create} from 'zustand';
import {devtools} from 'zustand/middleware';

import type {EcState, Address} from '../types';

const INITIAL_STATE = {
  ec: {
    id: 'aasd3453sdasld90asd09as8das0',
    representativeData: {
      cpf: '',
      cnpj: '',
      email: '',
      displayName: '',
      telephoneNumber: '',
      birthDate: '',
      isPartner: false
    },
    ecBasicData: {
      cnpj: '',
      legalName: '',
      fantasyName: '',
      primaryCnae: ''
    },
    partners: [],
    addresses: []
  },
  partnerSelected: {},
  partnersCompleted: []
};

export const useEcStore = create<EcState>()(
  devtools(
    (set) => ({
      ...INITIAL_STATE,

      setEcIdentity: (representativeData) =>
        set(
          (state) => ({
            ec: {
              ...state.ec,
              representativeData: {
                ...state.ec.representativeData,
                ...representativeData
              }
            }
          }),
          false,
          'ec/setEcIdentity'
        ),

      updateEc: (ec) =>
        set(
          (state) => ({
            ec: {
              ...state.ec,
              id: ec.id ?? state.ec.id,
              representativeData: ec.representativeData
                ? {...state.ec.representativeData, ...ec.representativeData}
                : state.ec.representativeData,
              ecBasicData: ec.ecBasicData
                ? {...state.ec.ecBasicData, ...ec.ecBasicData}
                : state.ec.ecBasicData,
              partners: ec.partners ? [...ec.partners] : state.ec.partners,
              addresses: ec.addresses ? [...ec.addresses] : state.ec.addresses
            }
          }),
          false,
          'ec/updateEc'
        ),

      updatePartners: (partners) =>
        set(
          (state) => ({
            ec: {
              ...state.ec,
              partners: [...partners]
            }
          }),
          false,
          'ec/updatePartners'
        ),

      selectPartner: (partner) =>
        set({partnerSelected: partner}, false, 'ec/selectPartner'),

      setPartnersCompleted: (partner) =>
        set(
          (state) => ({
            partnersCompleted: [...state.partnersCompleted, partner]
          }),
          false,
          'ec/setPartnersCompleted'
        ),

      updateAddress: (address) =>
        set(
          (state) => ({
            ec: {
              ...state.ec,
              addresses: Array.isArray(address)
                ? [...address]
                : [...state.ec.addresses]
            }
          }),
          false,
          'ec/updateAddress'
        ),

      setEmptyAddress: (address) =>
        set(
          (state) => ({
            ec: {
              ...state.ec,
              addresses: Array.isArray(address) ? [...address] : []
            }
          }),
          false,
          'ec/setEmptyAddress'
        ),

      updateAddressType: (addressType) =>
        set(
          (state) => {
            const index = state.ec.addresses.length - 1;

            if (index < 0) {
              return {
                ec: {
                  ...state.ec,
                  addresses: Array.isArray(addressType)
                    ? [...addressType]
                    : [addressType]
                }
              } as Partial<EcState>;
            }

            const updatedAddresses = [...state.ec.addresses];
            updatedAddresses[index] = {
              ...updatedAddresses[index],
              ...addressType
            } as Address;

            return {
              ec: {
                ...state.ec,
                addresses: updatedAddresses
              }
            } as Partial<EcState>;
          },
          false,
          'ec/updateAddressType'
        )
    }),
    {name: 'EcStore'}
  )
);

// Selectors
export const selectEc = (state: EcState) => state.ec;
export const selectEcId = (state: EcState) => state.ec.id;
export const selectRepresentativeData = (state: EcState) =>
  state.ec.representativeData;
export const selectEcBasicData = (state: EcState) => state.ec.ecBasicData;
export const selectPartners = (state: EcState) => state.ec.partners;
export const selectAddresses = (state: EcState) => state.ec.addresses;
export const selectPartnerSelected = (state: EcState) => state.partnerSelected;
export const selectPartnersCompleted = (state: EcState) =>
  state.partnersCompleted;
